import type { ColumnsType } from 'antd/es/table'
import type { RenderExpandIconProps } from 'rc-table/lib/interface'

import {
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteFilled,
  FileImageOutlined,
  FlagFilled,
  PauseCircleFilled,
  QuestionCircleOutlined,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Button, Dropdown, Input, Space, Table, Tag, Tooltip } from 'antd'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import React, { useCallback, useMemo, useReducer } from 'react'

import $api, { API_URL } from '../../API'
import { useDebounce } from '../../hook/use-debounce'
import { DateFilterDropdown } from '../notifications'
import { HumanReadableTime } from '../payment-files/release/components/human-readable-time'
import '../payment-files/release/index.css'
import dayjs from 'dayjs'
import { useAppDispatch } from '../../store'
import type { Attachment } from '../../store/previewer/types'
import * as previewActions from '../../store/previewer'

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

interface Group {
  id: string
  number: string
  name: string
  location: Location
}

interface Location {
  id: string
  name: string
  short_name: string
}

interface X937Info {
  checkId: string | null
  paidDate: string | null
}

interface EOB {
  id: string
  tin: string
  originalFilename: string
  runDate: string
  check: Check
  x937: X937Info
  claims: Claim[]
  group: Group
  isOnHold: boolean
  isFlaggedStopLoss: boolean
  lucentPaymentMethod: string
  zelisPaymentMethod?: string
  diagnostics: Diagnostic
  cursor: string
  syncedAt: string
}

interface Check {
  amount: number
  number: string
}

interface Diagnostic {
  count: number
  record_type: string[]
  claim_type: string[]
  clservpc: string[]
  run_date: string
  eep_provider_id: string | null
  eealp_provider_tin: string | null
}

interface Claim {
  id: string
  payee: Payee
  provider: Provider
  amount: number
}

interface Payee {
  id: string
  name: string
}

interface Provider {
  id: string
  name: string
}

interface State {
  searchQuery: string
  expandedRowIds: string[]

  filters: {
    location: string[]
    zelisPaymentMethod: string[]
    lucentPaymentMethod: string[]
    flags: string[]
    runDate: {
      from?: string
      to?: string
    }
  }

  pagination: {
    currentPage: number
    pageSize: number
  }

  refreshInterval?: number
  selectedEOBIds: string[]
  selectedClaimIds: string[]
}

const initialState: State = {
  searchQuery: '',
  expandedRowIds: [],

  filters: {
    location: [],
    zelisPaymentMethod: [],
    lucentPaymentMethod: [],
    flags: [],
    runDate: {}
  },

  pagination: {
    currentPage: 0,
    pageSize: 10
  },

  selectedEOBIds: [],
  selectedClaimIds: []
}

type Action =
  | { type: 'rows/expand'; payload: string[] }
  | { type: 'row/expand'; payload: string }
  | { type: 'row/collapse'; payload: string }
  | { type: 'eobIds/select'; payload: string[] }
  | { type: 'claimIds/select'; payload: string[] }
  | { type: 'pagination/currentPage/set'; payload: number }
  | { type: 'pagination/pageSize/set'; payload: number }
  | { type: 'filters/set'; payload: State['filters'] }
  | { type: 'filters/clear' }
  | { type: 'searchQuery/set'; payload: string }
  | { type: 'refreshInterval/set'; payload: number }
  | { type: 'refreshInterval/clear' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'rows/expand': {
      return {
        ...state,
        expandedRowIds: action.payload
      }
    }

    case 'row/expand': {
      return {
        ...state,
        expandedRowIds: [...state.expandedRowIds, action.payload]
          .sort((a, b) => a.localeCompare(b))
          .filter((v, i, a) => a.indexOf(v) === i)
      }
    }

    case 'row/collapse': {
      return {
        ...state,
        expandedRowIds: state.expandedRowIds.filter(id => id !== action.payload)
      }
    }

    case 'eobIds/select': {
      return {
        ...state,
        selectedEOBIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'claimIds/select': {
      return {
        ...state,
        selectedClaimIds: action.payload.sort((a, b) => a.localeCompare(b)).filter(uniquePredicate)
      }
    }

    case 'pagination/currentPage/set': {
      return {
        ...state,
        expandedRowIds: [],
        pagination: {
          ...state.pagination,
          currentPage: action.payload
        }
      }
    }

    case 'pagination/pageSize/set': {
      return {
        ...state,
        expandedRowIds: [],
        pagination: {
          ...state.pagination,
          currentPage: 0,
          pageSize: action.payload
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        filters: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchQuery: initialState.searchQuery,
        filters: initialState.filters,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'searchQuery/set': {
      return {
        ...state,
        searchQuery: action.payload,
        pagination: {
          ...state.pagination,
          currentPage: 0
        }
      }
    }

    case 'refreshInterval/set': {
      return {
        ...state,
        refreshInterval: action.payload
      }
    }

    case 'refreshInterval/clear': {
      return {
        ...state,
        refreshInterval: undefined
      }
    }

    default: {
      return state
    }
  }
}

const baseCellClassName = 'text-xs font-lato'
const baseHeaderCellClassName = 'text-[0.675rem] uppercase font-lato !bg-transparent'

interface Filter {
  value: string | number | boolean | null
  label?: string
  count?: number
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

interface HumanizeDurationOptions {
  short?: boolean
}

function humanizeDuration(ms: number, options?: HumanizeDurationOptions): string {
  const short = options?.short ?? false

  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    if (short) {
      return `${round(hours)}h`
    }

    return `${round(hours)} ${pluralize(round(hours), 'hour', 'hours')}`
  }

  if (minutes > 0) {
    if (short) {
      return `${round(minutes)}m`
    }

    return `${round(minutes)} ${pluralize(round(minutes), 'minute', 'minutes')}`
  }

  if (short) {
    return `${round(seconds)}s`
  }

  return `${round(seconds)} ${pluralize(round(seconds), 'second', 'seconds')}`
}

function humanizeLargeNumber(value: number, precision = 2): string {
  const suffixes = ['', 'K', 'M', 'B', 'T']

  let suffixIndex = 0

  while (value >= 1000 && suffixIndex < suffixes.length - 1) {
    value /= 1000
    suffixIndex++
  }

  return `${round(value, precision)}${suffixes[suffixIndex]}`
}

export function ClaimsPage(): React.ReactNode {
  const appDispatch = useAppDispatch()
  const [state, dispatch] = useReducer(reducer, initialState)

  const isDefaultFilters =
    state.searchQuery === initialState.searchQuery &&
    state.filters.location.length === 0 &&
    state.filters.zelisPaymentMethod.length === 0 &&
    state.filters.lucentPaymentMethod.length === 0 &&
    state.filters.flags.length === 0 &&
    (typeof state.filters.runDate.from !== 'string' || state.filters.runDate.from.length === 0) &&
    (typeof state.filters.runDate.to !== 'string' || state.filters.runDate.to.length === 0)

  const debouncedSearchQuery: string = useDebounce(state.searchQuery, 500)

  const query = useInfiniteQuery({
    queryKey: [
      'claims',
      {
        searchQuery: debouncedSearchQuery,
        filters: state.filters,
        pagination: state.pagination
      }
    ],

    async queryFn({ queryKey, signal, pageParam }) {
      const [, { searchQuery, filters, pagination }] = queryKey as QueryKeyWithParams<
        typeof queryKey
      >

      interface Response {
        data: EOB[]
        count: number
        total: number
        filters: {
          location: Filter[]
          zelisPaymentMethod: Filter[]
          lucentPaymentMethod: Filter[]
          flags: Filter[]
        }
      }

      const sp = new URLSearchParams([
        ...(searchQuery.trim().length > 0 ? [['search', searchQuery]] : []),
        ...filters.location.map(value => ['location', value]),
        ...filters.zelisPaymentMethod.map(value => ['zelis-payment-method', value]),
        ...filters.lucentPaymentMethod.map(value => ['lucent-payment-method', value]),
        ...filters.flags.map(value => ['flag', value]),
        ...(typeof filters.runDate.from === 'string' && filters.runDate.from.length > 0
          ? [['run-date.from', filters.runDate.from]]
          : []),
        ...(typeof filters.runDate.to === 'string' && filters.runDate.to.length > 0
          ? [['run-date.to', filters.runDate.to]]
          : []),
        ...(typeof pageParam === 'string' ? [['cursor', pageParam]] : []),
        ['page-size', pagination.pageSize.toString()],
        ['order-by', 'number']
      ])

      const response = await $api.get<Response>(`/claims?${sp.toString()}`, {
        signal
      })

      return response.data
    },

    getNextPageParam(lastPage) {
      return lastPage.data.at(-1)?.cursor ?? null
    },

    keepPreviousData: true
  })

  const handlePropagatedActionClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.stopPropagation()
    },
    []
  )

  const handlePdfPreviewClick = useCallback(
    (file: Attachment) => (): void => {
      appDispatch(previewActions.open(file))
    },
    [appDispatch]
  )

  const token = window.localStorage.getItem('token')

  const buildFileHref = useCallback(
    (checkId: string) => {
      return `${API_URL}/pinnacle/x937/checks/${checkId}/images?token=${encodeURIComponent(token ?? '')}`
    },
    [token]
  )

  const columns = useMemo<ColumnsType<EOB>>(
    () => [
      {
        title: 'EOB #',
        dataIndex: 'number',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Group #',
        dataIndex: ['group', 'number'],
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: ['group', 'location', 'shortName'],
        align: 'center',

        filters: (query.data?.pages.at(-1)?.filters.location ?? []).map(filter => ({
          text: (
            <div className='flex flex-row justify-between items-center w-full min-w-[10rem] gap-4'>
              <span className='inline-flex gap-1'>
                {filter.label ?? filter.value ?? <span className='text-gray-400'>(N/A)</span>}
              </span>

              <span
                className={cx({
                  'text-gray-500': (filter.count ?? 0) > 0,
                  'text-gray-300': (filter.count ?? 0) === 0
                })}
              >
                {(filter.count ?? 0) || '—'}
              </span>
            </div>
          ),
          value: filter.value ?? 'null'
        })),

        filterMultiple: true,
        filteredValue: state.filters.location,

        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Payment File',
        dataIndex: 'originalFilename',
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Run Date',
        key: 'runDate',
        dataIndex: 'runDate',
        align: 'center',

        filterDropdown: DateFilterDropdown,

        filteredValue:
          (typeof state.filters.runDate.from === 'string' &&
            state.filters.runDate.from.length > 0) ||
          (typeof state.filters.runDate.to === 'string' && state.filters.runDate.to.length > 0)
            ? [state.filters.runDate.from ?? '', state.filters.runDate.to ?? '']
            : [],

        render(value: string) {
          return format(parseISO(value), 'MM/dd/yyyy')
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Zelis Payment Method',
        dataIndex: 'zelisPaymentMethod',
        align: 'center',

        filters: (query.data?.pages.at(-1)?.filters.zelisPaymentMethod ?? [])
          .map(filter => ({
            ...filter,
            label:
              filter.value === 'N/A' ? <span className='text-gray-400'>(N/A)</span> : filter.value
          }))
          .map(filter => ({
            text: (
              <div className='flex flex-row justify-between items-center w-full min-w-[10rem] gap-4'>
                <span className='inline-flex gap-1'>
                  {filter.label ?? filter.value ?? <span className='text-gray-400'>(N/A)</span>}
                </span>

                <span
                  className={cx({
                    'text-gray-500': (filter.count ?? 0) > 0,
                    'text-gray-300': (filter.count ?? 0) === 0
                  })}
                >
                  {(filter.count ?? 0) || '—'}
                </span>
              </div>
            ),
            value: filter.value ?? 'null'
          })),

        filterMultiple: true,
        filteredValue: state.filters.zelisPaymentMethod,

        render(value) {
          if (typeof value !== 'string' || value.trim().length === 0) {
            return <EmptyCellValue />
          }

          return value
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Lucent Payment Method',
        dataIndex: 'lucentPaymentMethod',
        align: 'center',

        filters: (query.data?.pages.at(-1)?.filters.lucentPaymentMethod ?? []).map(filter => ({
          text: (
            <div className='flex flex-row justify-between items-center w-full min-w-[10rem] gap-4'>
              <span className='inline-flex gap-1'>
                {filter.label ?? filter.value ?? <span className='text-gray-400'>(N/A)</span>}
              </span>

              <span
                className={cx({
                  'text-gray-500': (filter.count ?? 0) > 0,
                  'text-gray-300': (filter.count ?? 0) === 0
                })}
              >
                {(filter.count ?? 0) || '—'}
              </span>
            </div>
          ),
          value: filter.value ?? 'null'
        })),

        filterMultiple: true,
        filteredValue: state.filters.lucentPaymentMethod,

        render(value) {
          if (typeof value !== 'string' || value.trim().length === 0) {
            return <EmptyCellValue />
          }

          return value
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Check #',
        dataIndex: ['check', 'number'],
        align: 'center',
        render(value) {
          if (typeof value !== 'string' || value.trim().length === 0) {
            return <EmptyCellValue />
          }

          return value
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Check Amount',
        dataIndex: ['check', 'amount'],
        align: 'right',
        render(value: number) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }

          return currencyFormatter.format(value)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Paid Date',
        dataIndex: ['x937', 'paidDate'],
        align: 'center',
        render(value: string, record) {
          if (!value || !record.x937.checkId) {
            return <EmptyCellValue />
          }

          return (
            <div className='flex flex-row gap-2 justify-center items-center'>
              {dayjs(value).format('MM/DD/YYYY')}

              <div onClick={handlePropagatedActionClick}>
                <Tooltip title='View Checks'>
                  <button
                    type='button'
                    onClick={handlePdfPreviewClick({
                      mimeType: 'image/tiff',
                      name: 'Check images',
                      href: buildFileHref(record.x937.checkId)
                    })}
                  >
                    <FileImageOutlined className='text-lg text-blue-500 cursor-pointer hover:opacity-75' />
                  </button>
                </Tooltip>
              </div>
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Flags',
        key: 'flags',
        align: 'center',

        filters: [
          {
            value: 'on-hold',
            text: (
              <div className='flex flex-row justify-between items-center w-full min-w-[10rem] gap-4'>
                <span className='inline-flex gap-1'>
                  <PauseCircleFilled className='text-sm text-yellow-500' />
                  On Hold
                </span>

                <span
                  className={cx({
                    'text-gray-500':
                      (query.data?.pages
                        .at(-1)
                        ?.filters.flags.find(filter => filter.value === 'on-hold')?.count ?? 0) > 0,
                    'text-gray-300':
                      (query.data?.pages
                        .at(-1)
                        ?.filters.flags.find(filter => filter.value === 'on-hold')?.count ?? 0) ===
                      0
                  })}
                >
                  {(query.data?.pages
                    .at(-1)
                    ?.filters.flags.find(filter => filter.value === 'on-hold')?.count ??
                    0) ||
                    '—'}
                </span>
              </div>
            )
          },
          {
            value: 'stop-loss',
            text: (
              <div className='flex flex-row justify-between items-center w-full min-w-[10rem]'>
                <span className='inline-flex gap-1'>
                  <FlagFilled className='text-sm text-lucent-red' />
                  Stop Loss
                </span>

                <span
                  className={cx({
                    'text-gray-500':
                      (query.data?.pages
                        .at(-1)
                        ?.filters.flags.find(filter => filter.value === 'stop-loss')?.count ?? 0) >
                      0,
                    'text-gray-300':
                      (query.data?.pages
                        .at(-1)
                        ?.filters.flags.find(filter => filter.value === 'stop-loss')?.count ??
                        0) === 0
                  })}
                >
                  {(query.data?.pages
                    .at(-1)
                    ?.filters.flags.find(filter => filter.value === 'stop-loss')?.count ??
                    0) ||
                    '—'}
                </span>
              </div>
            )
          }
        ],

        filterMultiple: true,
        filteredValue: state.filters.flags,

        render(_, record) {
          return (
            <div className='flex gap-2 justify-center items-center'>
              {record.isOnHold && (
                <Tooltip title='On hold'>
                  <PauseCircleFilled className='text-sm text-yellow-500' />
                </Tooltip>
              )}

              {record.isFlaggedStopLoss && (
                <Tooltip title='Flagged as Stop Loss'>
                  <FlagFilled className='text-sm text-lucent-red' />
                </Tooltip>
              )}
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        key: 'info',
        dataIndex: 'diagnostics',
        align: 'center',
        render(value: Record<string, unknown> = {}, record) {
          const keys: Array<keyof typeof value> = [
            'record_type',
            'claim_type',
            'clservpc',
            'run_date',
            'eep_provider_id',
            'eealp_provider_tin'
          ]

          interface Stat {
            key: string
            values: React.ReactNode[]
          }

          const aggregated: Stat[] = keys
            .map(key => {
              const values = value[key]

              return {
                key,
                values: (Array.isArray(values) ? values : [values]).filter(
                  Boolean
                ) as React.ReactNode[]
              }
            })
            .concat({
              key: 'synced_at',
              values: [
                format(parseISO(record.syncedAt), 'MM/dd/yyyy HH:mm:ss'),

                <HumanReadableTime key={1} value={parseISO(record.syncedAt)} />
              ]
            })

          return (
            <div className='flex gap-2 justify-center items-center'>
              <Tooltip
                color='#ffffff'
                title={
                  <div className='flex overflow-y-scroll flex-col gap-2 px-3 py-2 max-w-fit'>
                    {aggregated.map(({ key, values }) => (
                      <div key={key}>
                        <span className='font-mono text-xs font-medium text-lucent-gray'>
                          {key}
                        </span>

                        <div className='flex flex-row gap-1 pt-1'>
                          {values.length === 0 && (
                            <span className='text-xs font-lato text-lucent-gray'>N/A</span>
                          )}

                          {values.map(value => (
                            <Tag key={value?.toString()} className='text-xs font-lato'>
                              {value}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              >
                <QuestionCircleOutlined className='p-2 -m-2 text-sm opacity-25 transition-opacity text-lucent-text hover:opacity-100' />
              </Tooltip>
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      }
    ],
    [
      state.filters,
      query.data?.pages,
      buildFileHref,
      handlePdfPreviewClick,
      handlePropagatedActionClick
    ]
  )

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex justify-between items-start'>
          <div className='flex gap-6 items-center'>
            <h1 className='text-2xl'>Claims Search</h1>
          </div>
        </div>

        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search by group, location, payment file, check number, EOB number, claim number, payee, or provider'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    state.searchQuery.length > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        onClick={() => {
                          dispatch({
                            type: 'searchQuery/set',
                            payload: ''
                          })
                        }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  value={state.searchQuery}
                  onChange={event => {
                    dispatch({
                      type: 'searchQuery/set',
                      payload: event.target.value
                    })
                  }}
                />
              </div>

              <Space>
                {!isDefaultFilters && (
                  <Button
                    type='default'
                    onClick={() => {
                      dispatch({
                        type: 'filters/clear'
                      })

                      // void queryClient.invalidateQueries([ 'claims' ])
                    }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled className='text-lucent-primary' />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )}

                <Dropdown.Button
                  type='default'
                  disabled={query.isFetching}
                  onClick={() => {
                    void query.refetch()
                    // void queryClient.invalidateQueries([ 'check-runs', 'meta', 'totals' ])
                  }}
                  icon={
                    <CaretDownOutlined
                      className={cx(
                        'w-[12px] h-[12px] !translate-y-[2px]',
                        typeof state.refreshInterval === 'number' && 'text-lucent-primary',
                        typeof state.refreshInterval !== 'number' && 'text-lucent-gray'
                      )}
                    />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        if (state.refreshInterval === seconds * 1000) {
                          return <CheckOutlined className='ml-2 text-lucent-primary' />
                        }

                        return null
                      },
                      onClick() {
                        if (state.refreshInterval === seconds * 1000) {
                          dispatch({
                            type: 'refreshInterval/clear'
                          })

                          return
                        }

                        dispatch({
                          type: 'refreshInterval/set',
                          payload: seconds * 1000
                        })
                      },
                      label: humanizeDuration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>

                      {typeof state.refreshInterval === 'number' && (
                        <span className=''>
                          <span className='mx-1.5'>|</span>
                          Auto {humanizeDuration(state.refreshInterval, { short: true })}
                        </span>
                      )}
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>

          <>
            <Table
              size='small'
              rowKey='number'
              className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
              rowSelection={{
                columnWidth: 4.5 * 16,
                hideSelectAll: false,

                // selectedRowKeys: batchSelectionContext.selectedEOBIds,

                onSelect(_record, _selected, _selectedRows, event) {
                  event.stopPropagation()
                },

                onChange() {
                  //   body
                },

                getCheckboxProps() {
                  return {}
                }
              }}
              columns={columns}
              loading={query.isFetching}
              dataSource={query.data?.pages.flatMap(page => page.data) ?? []}
              pagination={false}
              expandable={{
                // expandedRowKeys: state.expandedRowIds,
                onExpandedRowsChange(expandedRowKeys) {
                  dispatch({
                    type: 'rows/expand',
                    payload: expandedRowKeys.map(key => key.toString(10))
                  })
                },
                expandIcon: ExpandIcon,
                showExpandColumn: false,
                expandRowByClick: true,
                expandedRowRender(record, _, __, expanded) {
                  if (!expanded) {
                    return null
                  }

                  return <EOBExpandableRow claims={record.claims as Claim[]} />
                }
                // expandedRowRender (record, index, indent, expanded) {
                //   if (!expanded) {
                //     return null
                //   }

                //   return (
                //     <EOBExpandableRow
                //       claims={record.claims}
                //     />
                //   )
                // }
              }}
              onChange={(pagination, filters, _, extra) => {
                if (extra.action === 'filter') {
                  dispatch({
                    type: 'filters/set',
                    payload: {
                      location: (filters.location ?? []) as string[],
                      lucentPaymentMethod: (filters.lucentPaymentMethod ?? []) as string[],
                      zelisPaymentMethod: (filters.zelisPaymentMethod ?? []) as string[],
                      flags: (filters.flags ?? []) as string[],
                      runDate: {
                        from: filters.runDate?.[0] as string | undefined,
                        to: filters.runDate?.[1] as string | undefined
                      }
                    }
                  })
                }

                if (extra.action === 'paginate') {
                  dispatch({
                    type: 'pagination/currentPage/set',
                    payload: (pagination.current ?? 1) - 1
                  })

                  if ((pagination.pageSize ?? 10) !== state.pagination.pageSize) {
                    dispatch({
                      type: 'pagination/pageSize/set',
                      payload: pagination.pageSize ?? 10
                    })
                  }
                }
              }}
              summary={data => {
                const visibleCount = data.length
                const filteredCount = query.data?.pages.at(-1)?.count ?? 0
                const totalCount = query.data?.pages.at(-1)?.total ?? 0

                const parts: string[] = []

                if (visibleCount === filteredCount) {
                  if (visibleCount === 0) {
                    parts.push('No EOBs found')
                  }

                  if (visibleCount > 0) {
                    parts.push(
                      `Showing all ${visibleCount} ${pluralize(visibleCount, 'EOB', 'EOBs')}`
                    )
                  }
                }

                if (visibleCount !== filteredCount) {
                  const humanized = humanizeLargeNumber(filteredCount, 2)
                  parts.push(`Showing first ${visibleCount} of ~${humanized} EOBs`)
                }

                if (filteredCount !== totalCount) {
                  const humanized = humanizeLargeNumber(totalCount, 2)
                  parts.push(`Total ~${humanized} EOBs`)
                }

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell
                      index={0}
                      colSpan={columns.length + 1}
                      className='text-sm font-lato text-gray-500 border-b-0!'
                    >
                      <div className='flex gap-4 justify-between items-center px-2 py-2 w-full'>
                        <span>{parts.join(' • ')}</span>

                        <Tooltip
                          title={
                            (query.data?.pages.at(-1)?.data.length ?? 0) > 0
                              ? undefined
                              : 'No more EOBs to load'
                          }
                        >
                          <Button
                            type='default'
                            size='small'
                            disabled={
                              query.isFetching ||
                              query.isFetchingNextPage ||
                              (query.data?.pages.at(-1)?.data.length ?? 0) === 0
                            }
                            loading={query.isFetchingNextPage}
                            onClick={() => {
                              void query.fetchNextPage()
                            }}
                          >
                            Load more
                          </Button>
                        </Tooltip>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )
              }}
            />
          </>
        </div>
      </div>
    </>
  )
}

function EmptyCellValue(): JSX.Element {
  return <span className='text-gray-400 font-lato'>—</span>
}

function uniquePredicate<T>(value: T, index: number, array: T[]): boolean {
  return array.indexOf(value) === index
}

function round(value: number, precision = 2): number {
  const m = 10 ** precision
  return Math.round(value * m) / m
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ExpandIcon(props: RenderExpandIconProps<any>): JSX.Element | null {
  if (!props.expandable) {
    return null
  }

  return (
    <button
      type='button'
      className='flex justify-center items-center p-1 ml-2'
      onClick={event => {
        props.onExpand(props.record, event)
      }}
    >
      <CaretDownOutlined className={cx('text-gray-300 text-xs', props.expanded && 'rotate-180')} />
    </button>
  )
}

type EOBExpandableRowProps = Pick<EOB, 'claims'>

function EOBExpandableRow({ claims }: EOBExpandableRowProps): JSX.Element {
  const columns = useMemo<ColumnsType<Claim>>(
    () => [
      {
        title: 'Claim #',
        dataIndex: 'number',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Payee',
        dataIndex: ['payee', 'name'],
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Provider',
        dataIndex: ['provider', 'name'],
        align: 'center',
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      {
        title: 'Amount',
        dataIndex: 'amount',
        align: 'right',
        render(value: number) {
          return currencyFormatter.format(value)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      }
    ],
    []
  )

  return (
    <Table
      size='small'
      rowKey='number'
      className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
      rowSelection={{
        columnWidth: 5.5 * 16,
        selectedRowKeys: [],
        hideSelectAll: true,

        onSelect(_record, _selected, _selectedRows, event) {
          event.stopPropagation()
        },

        getCheckboxProps() {
          return {
            disabled: true,
            className: 'hidden'
          }
        }
      }}
      columns={columns}
      dataSource={claims}
      pagination={false}
    />
  )
}

type QueryKeyWithParams<T> = T extends Array<string | infer U> ? [string, U] : never
